/**
 * Search operator parsing — DESIGN.md §12.4 (R43).
 *
 * "the query string supports inline operators parsed into Filter ops —
 * `assignee:tom`, `status:"in testing"`, `tag:regression`, `project:TLC`,
 * `is:blocked`, `is:completed`, `before:2026-09-01`, `after:`, `due:today` —
 * combinable with free text (`login bug assignee:tom is:blocked`). Same syntax
 * works in ⌘K and the search page; **unknown operators degrade to plain text,
 * never error**."
 *
 * That last clause is why this module lives in `packages/shared` and produces
 * a *description* of the query rather than SQL: the parse is pure and total,
 * the client can render what it understood back to the user, and the server
 * resolves names to ids — which is the only part that needs a database.
 */
import { looksLikeCardMark } from './card-mark.js';
/** Operators the parser recognises. Anything else stays free text. */
export const SEARCH_OPERATORS = [
    'assignee',
    'creator',
    'status',
    'tag',
    'project',
    'priority',
    'is',
    'before',
    'after',
    'due',
    'in',
];
/**
 * Tokenise on whitespace, respecting double quotes so `status:"in testing"`
 * survives. Deliberately hand-rolled rather than a regex over the whole
 * string: quoted values can contain colons, and a single pass is easier to
 * reason about than a pattern that has to.
 */
function tokenize(input) {
    const tokens = [];
    let current = '';
    let quoted = false;
    for (const char of input) {
        if (char === '"') {
            quoted = !quoted;
            continue;
        }
        if (!quoted && /\s/.test(char)) {
            if (current !== '')
                tokens.push(current);
            current = '';
            continue;
        }
        current += char;
    }
    if (current !== '')
        tokens.push(current);
    return tokens;
}
const OPERATOR_SET = new Set(SEARCH_OPERATORS);
export function parseSearchQuery(input) {
    const terms = [];
    const words = [];
    let cardNumber = null;
    let cardMark = null;
    for (const token of tokenize(input)) {
        const negated = token.startsWith('-');
        const body = negated ? token.slice(1) : token;
        // `#1042` anywhere short-circuits to that card (§12.4).
        //
        // #34 — the `#` is optional. People type the number they read in a
        // standup or a commit message, and requiring the sigil made `1042` a
        // full-text search that usually found nothing. The card is offered *as
        // well as* the text hits rather than instead of them, so a bare `404`
        // still finds the cards that talk about 404s — it just puts card #404 at
        // the top if it exists.
        const numberMatch = /^#?(\d{1,12})$/.exec(body);
        if (numberMatch) {
            /*
             * #54 — eight digits that read as a date are the *identifier mark*, not
             * a card number. `26090101` used to be looked up as card #26090101,
             * which does not exist, so the one thing the mark was added for —
             * "people like Garry can easily find cards by identifier" — fell back to
             * a full-text search. Both are still offered; the mark wins where it
             * resolves, because it is the more specific reading.
             */
            if (looksLikeCardMark(numberMatch[1]))
                cardMark = numberMatch[1];
            cardNumber = Number(numberMatch[1]);
            words.push(body);
            continue;
        }
        const colon = body.indexOf(':');
        if (colon > 0) {
            const name = body.slice(0, colon).toLowerCase();
            const value = body.slice(colon + 1);
            if (OPERATOR_SET.has(name) && value !== '') {
                terms.push({ operator: name, value, negated });
                continue;
            }
        }
        // Unknown operator, or a bare word: free text. Never an error.
        words.push(token);
    }
    return { text: words.join(' ').trim(), terms, cardNumber, cardMark };
}
/**
 * Render a parsed query back to its canonical string. Used by ⌘K to show what
 * it understood, and to round-trip a saved search without drift.
 */
export function formatSearchQuery(parsed) {
    const parts = parsed.terms.map((term) => {
        const value = /\s/.test(term.value) ? `"${term.value}"` : term.value;
        return `${term.negated ? '-' : ''}${term.operator}:${value}`;
    });
    if (parsed.text)
        parts.unshift(parsed.text);
    return parts.join(' ');
}
/** Values `is:` understands. Anything else is ignored rather than rejected. */
export const IS_VALUES = [
    'blocked',
    'completed',
    'open',
    'assigned',
    'unassigned',
    'mine',
];
/** Relative values `due:` understands, mapped onto ViewConfig's tokens. */
export const DUE_VALUES = {
    today: 'today',
    tomorrow: 'tomorrow',
    yesterday: 'yesterday',
    week: 'thisWeek',
    thisweek: 'thisWeek',
    nextweek: 'nextWeek',
    month: 'thisMonth',
    overdue: 'overdue',
};
//# sourceMappingURL=search-query.js.map